import { DetailBreadcrumb } from "../components/DetailBreadcrumb";
import { ListingProfileCard } from "../components/ListingProfileCard";
import { ClosedPostNotice } from "../components/ClosedPostNotice";
import { useEffect, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { useListing } from "../lib/useListing";
import { Bookmark, CalendarDays, Facebook, House, Link2, Linkedin, Loader2, MapPin, Send, Users, X } from "lucide-react";
import { ImageCarousel } from "../components/ImageCarousel";
import { Feature } from "../components/ui/kit";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { MobileActionBar } from "../components/MobileActionBar";
import { JobDetailMobile } from "../components/JobDetailMobile";
import { ResumeModal } from "../components/DashboardProfile";
import { PreviousWork } from "../components/PreviousWork";
import ReportButton from "../components/ReportButton";
import { SaveButton } from "../components/SaveButton";
import { api } from "../lib/api";
import { copyShareLink } from "../lib/actions";
import { useAuth } from "../lib/auth";
import { useInPageActionVisibility } from "../lib/useInPageActionVisibility";
import { formatRelativeTime } from "../lib/relativeTime";
import { resetMeta, setMeta } from "../lib/seo";
import { jobs as mockJobs } from "../data/mock";
import { useI18n } from "../lib/i18n";
import { type ApplicationStatusState, type JobApplication, type JobListing, type EmployerProfile, type RelatedJob, isRelatedJob, employerFromResponse, errorText } from "./job-detail/model";
import { RelatedJobs } from "./job-detail/RelatedJobs";
import { EmploymentLink } from "../components/EmploymentLink";
export type { JobListing, EmployerProfile, RelatedJob } from "./job-detail/model";

export default function JobDetail() {
  const { t } = useI18n();
  const [, params] = useRoute("/j/:id");
  const id = params?.id || "";
  const { item, state } = useListing("jobs", id, mockJobs);
  const job = item as JobListing | null; // useListing's existing API boundary returns an untyped item.
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [myApp, setMyApp] = useState<JobApplication | null>(null);
  const [showApply, setShowApply] = useState(false);
  const [showResumes, setShowResumes] = useState(false);
  const [coverNote, setCoverNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [applicationStatusState, setApplicationStatusState] = useState<ApplicationStatusState>("loading");
  const [applicationStatusJobId, setApplicationStatusJobId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [employer, setEmployer] = useState<EmployerProfile | null>(null);
  const [relatedJobs, setRelatedJobs] = useState<RelatedJob[]>([]);
  const [shareMessage, setShareMessage] = useState<string | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches);
  const { actionRef: applyCtaRef, isVisible: isInPageApplyVisible } = useInPageActionVisibility<HTMLDivElement>(isMobileViewport ? null : job?.id);
  useEffect(() => {
    const viewport = window.matchMedia("(max-width: 639px)");
    const updateViewport = () => setIsMobileViewport(viewport.matches);
    viewport.addEventListener("change", updateViewport);
    return () => viewport.removeEventListener("change", updateViewport);
  }, []);
  const isWorker = !!user && (user.capabilities || []).includes("find_work");
  const isOwner = !!user && !!job?.ownerUserId && job.ownerUserId === user.id;
  const jobOpen = (job?.status || "open") === "open";
  const ownerUserId = Number(job?.ownerUserId);
  useEffect(() => {
    if (job) setMeta({ title: `${job.title} — KontrataPH`, description: [job.company, job.city, job.salary].filter(Boolean).join(" · ") || undefined });
    return () => resetMeta();
  }, [job]);
  useEffect(() => {
    if (!user || !job?.id || !isWorker || isOwner) {
      setMyApp(null);
      setApplicationStatusJobId(null);
      setApplicationStatusState("ready");
      return;
    }
    let cancelled = false;
    setApplicationStatusJobId(job.id);
    setApplicationStatusState("loading");
    api.myJobApplication(job.id)
      .then((application) => {
        if (cancelled) return;
        setMyApp(application);
        setApplicationStatusState("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setMyApp(null);
        setApplicationStatusState("error");
      });
    return () => { cancelled = true; };
  }, [user, job?.id, isWorker, isOwner]);
  useEffect(() => {
    if (job?.postingIdentity?.kind === "business" || !Number.isSafeInteger(ownerUserId) || ownerUserId <= 0) {
      setEmployer(null);
      return;
    }
    let cancelled = false;
    setEmployer(null);
    Promise.all([api.getProfile(ownerUserId), api.userReviews(ownerUserId, "jobs")])
      .then(([response, rating]) => { if (!cancelled) setEmployer(employerFromResponse({ ...response, rating })); })
      .catch(() => { if (!cancelled) setEmployer(null); });
    return () => { cancelled = true; };
  }, [ownerUserId, job?.postingIdentity?.kind]);
  useEffect(() => {
    if (!job?.id) return;
    let cancelled = false;
    setRelatedJobs([]);
    api.listPage("jobs", { limit: 18 })
      .then((response) => {
        if (cancelled) return;
        const tags = Array.isArray(job.tags) ? job.tags.map((tag) => tag.toLowerCase()) : [];
        const score = (related: RelatedJob) => related.tags?.filter((tag) => tags.includes(tag.toLowerCase())).length || 0;
        const matches = response.items.filter(isRelatedJob).filter((related) => related.id !== job.id && score(related) > 0);
        setRelatedJobs(matches.sort((a, b) => score(b) - score(a)).slice(0, 3));
      })
      .catch(() => { if (!cancelled) setRelatedJobs([]); });
    return () => { cancelled = true; };
  }, [job?.id, job?.tags]);
  if (state === "loading") return <DetailLoading label="Loading job…" />;
  if (state === "notfound" || !job) return <DetailNotFound title="Job not found" backHref="/marketplace?tab=Jobs" backLabel="Browse Jobs" />;
  const tags = Array.isArray(job.tags) ? job.tags.filter((tag): tag is string => typeof tag === "string") : [];
  const skillTags = tags.filter((tag) => !/^(on-site|remote|hybrid)$/i.test(tag));
  const description = job.description?.trim() || `${job.postingIdentity?.kind === "business" ? job.postingIdentity.name : job.company} is hiring a ${job.title} in ${job.city}. This is a ${(job.type || "job").toLowerCase()} role.`;
  const posted = formatRelativeTime(job.createdAt, job.posted);
  const applicationForJob = applicationStatusJobId === job.id ? myApp : null;
  const statusForJob = applicationStatusJobId === job.id ? applicationStatusState : "loading";
  const hasActiveApp = !!applicationForJob && ["applied", "shortlisted", "accepted"].includes(applicationForJob.status);
  const profileHref = Number.isSafeInteger(ownerUserId) && ownerUserId > 0 ? `/u/${ownerUserId}` : null;
  const employerName = employer?.name || job.company;
  const avatarSrc = api.apiAssetUrl(employer?.profileImageUrl);
  const shareUrl = encodeURIComponent(window.location.href);
  const jobId = job.id;
  const workMode = tags.find((tag) => /^(on-site|remote|hybrid)$/i.test(tag));
  async function apply() {
    setBusy(true); setErr(null);
    try {
      const response = await api.applyToJob(jobId, coverNote.trim() || undefined);
      setMyApp(response?.application ?? null);
      setShowApply(false);
      setCoverNote("");
    } catch (error: unknown) { setErr(errorText(error, "Could not apply.")); }
    finally { setBusy(false); }
  }
  async function withdraw() {
    setBusy(true); setErr(null);
    try {
      const response = await api.withdrawApplication(jobId);
      setMyApp(response?.application ?? null);
    } catch (error: unknown) { setErr(errorText(error, "Could not withdraw.")); }
    finally { setBusy(false); }
  }
  async function copyLink() {
    const copied = await copyShareLink();
    setShareMessage(copied ? "Link copied" : "Could not copy link");
  }
  function ApplyBox({ mobile = false }: { mobile?: boolean }) {
    const buttonClassName = mobile ? "btn-red h-12 w-full rounded-xl" : "btn-red w-full";
    if (!user) return <button onClick={() => navigate("/login")} className={buttonClassName}>{!mobile && <Send className="w-4 h-4" />}Sign in to apply</button>;
    if (isOwner) return <Link href={`/dashboard?view=hiring&jobId=${encodeURIComponent(job.id)}`} className={`${buttonClassName} flex justify-center`}>View applicants</Link>;
    if (!isWorker) return <div className="text-sm text-mut">Enable <b>Find Work</b> on your account to apply for jobs.</div>;
    if (statusForJob === "loading") return <div role="status" className="chip w-full justify-center py-2 bg-navy-50 text-mut"><Loader2 className="w-4 h-4 animate-spin" />Checking application status…</div>;
    if (statusForJob === "error") return <div role="status" className="chip w-full justify-center py-2 bg-navy-50 text-mut">Unable to verify application status. Refresh to try again.</div>;
    if (hasActiveApp && applicationForJob) {
      const label: Record<string, string> = { applied: "Applied", shortlisted: t("activity.status.shortlisted"), accepted: "Hired 🎉" };
      return <div className="text-center">
        <div className={`chip w-full justify-center py-2 mb-2 ${applicationForJob.status === "accepted" ? "bg-emerald-100 text-emerald-700" : "bg-navy-50 text-navy"}`}>{label[applicationForJob.status]}</div>
        {applicationForJob.status === "accepted" && <EmploymentLink applicationId={applicationForJob.id} />}
        {applicationForJob.status !== "accepted" && <button onClick={withdraw} disabled={busy} className="btn-outline w-full text-brand-red disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}Withdraw application</button>}
      </div>;
    }
    if (!jobOpen) return <div className="chip w-full justify-center py-2 bg-navy-50 text-mut">Applications closed</div>;
    return <button onClick={() => setShowApply(true)} className={buttonClassName}>{!mobile && <Send className="w-4 h-4" />}{applicationForJob ? t("activity.applyAgain") : t("activity.apply")}</button>;
  }
  return (
    <>
    {isMobileViewport && <JobDetailMobile job={job} employer={employer} relatedJobs={relatedJobs} description={description} posted={posted} workMode={workMode}
      isOwner={isOwner} isSignedIn={!!user} applyAction={<ApplyBox mobile />}
      error={err}
      onSaveSignIn={() => navigate("/login")} />}
    {!isMobileViewport && <div className={`container-x py-5 ${!isOwner && !isInPageApplyVisible ? "pb-24 lg:pb-6" : ""}`}>
      <ClosedPostNotice post={job || {}} />
      <DetailBreadcrumb title={job.title} fallback={{ href: "/marketplace?tab=Jobs", label: "Jobs" }} className="mb-3 text-sm" />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.9fr)_minmax(310px,0.98fr)] items-start">
        <div className="space-y-4 min-w-0">
          <ImageCarousel type="job" id={id} fallbackSrc={job.coverImage} compact narrowThumbnails />
          <section className="card p-4">
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-2xl font-extrabold text-navy leading-tight">{job.title}</h1>
              {user ? <SaveButton kind="job" refId={job.id} className="h-9 w-9 shrink-0 rounded-lg border border-line" iconClassName="h-4 w-4" />
                : <button type="button" onClick={() => navigate("/login")} aria-label="Sign in to save job" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line text-mut hover:text-navy"><Bookmark className="h-4 w-4" /></button>}
            </div>
          </section>
          <section className="card p-5"><h2 className="font-bold text-navy text-lg mb-2">{t("detail.job.description")}</h2><p className="text-sm text-mut whitespace-pre-line leading-relaxed">{description}</p></section>
          <section className="card p-5"><h2 className="font-bold text-navy text-lg mb-3">{t("detail.job.skillsResponsibilities")}</h2>
            {skillTags.length > 0 ? <ul className="space-y-2">{skillTags.map((tag) => <Feature key={tag}>{tag}</Feature>)}</ul>
              : <p className="text-sm text-mut">{t("detail.job.noRequirements")}</p>}
          </section>
          {job.benefits?.trim() && <section className="card p-5"><h2 className="font-bold text-navy text-lg mb-2">{t("detail.job.benefits")}</h2><p className="text-sm text-mut whitespace-pre-line">{job.benefits}</p></section>}
          <PreviousWork text={job.previousWork} type="job" id={id} />
          <RelatedJobs jobs={relatedJobs} />
        </div>
        <aside className="lg:sticky lg:top-24 self-start h-fit space-y-4 desktop-sidebar-scroll min-w-0">
          <ListingProfileCard postingIdentity={job.postingIdentity} title={t("detail.job.aboutEmployer")}
            profile={{ name: employerName, role: t("detail.profile.employer"), location: employer?.city || job.city, memberSince: employer?.memberSince, imageUrl: avatarSrc, href: profileHref, identityVerified: employer?.identityVerified, verifiedProfessions: employer?.verifiedProfessions }}
            statistics={[{ label: t("detail.profile.jobsPosted"), value: job.employerStats?.jobsPosted }, { label: t("detail.profile.peopleHired"), value: job.employerStats?.peopleHired }]} />
          <section className="card p-5">
            <div className="text-xs text-mut">Offered salary</div><div className="text-xl font-extrabold text-navy mt-1">{job.salary || "Negotiable"}</div>
            <div className="flex flex-col gap-2 mt-4">
              <div ref={applyCtaRef}><ApplyBox /></div>
            </div>
            {err && <p className="text-xs text-brand-red text-center mt-2">{err}</p>}
          </section>
          <section className="card p-5"><h2 className="font-bold text-navy mb-3">Work setup</h2><div className="divide-y divide-line text-sm">
            <div className="flex items-center justify-between gap-2 py-2"><span className="inline-flex items-center gap-3 text-mut"><House className="w-4 h-4 text-navy" />Work arrangement</span><strong>{workMode || "Not specified"}</strong></div>
            <div className="flex items-start gap-3 py-2"><MapPin className="w-4 h-4 mt-0.5 text-navy shrink-0" />{job.city}</div>
            <div className="flex items-center justify-between gap-2 py-2"><span className="inline-flex items-center gap-3 text-mut"><Users className="w-4 h-4 text-navy" />Applicants</span><strong>{job.applicants}</strong></div>
            <div className="flex items-center justify-between gap-2 py-2"><span className="inline-flex items-center gap-3 text-mut"><CalendarDays className="w-4 h-4 text-navy" />Posted</span><strong>{posted}</strong></div>
          </div></section>
          <section className="card p-5"><h2 className="font-bold text-navy">Share this job</h2><p className="text-sm text-mut mt-1">Help others find this opportunity.</p>
            <div className="grid grid-cols-4 gap-1 mt-4 text-center text-xs text-mut">
              <button type="button" onClick={copyLink} className="flex flex-col items-center gap-2"><span className="w-11 h-11 rounded-full bg-navy-50 grid place-items-center text-navy"><Link2 className="w-5 h-5" /></span>Copy link</button>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2"><span className="w-11 h-11 rounded-full bg-navy-50 grid place-items-center text-blue-700"><Facebook className="w-5 h-5" /></span>Facebook</a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2"><span className="w-11 h-11 rounded-full bg-navy-50 grid place-items-center text-blue-700"><Linkedin className="w-5 h-5" /></span>LinkedIn</a>
              <a href={`https://twitter.com/intent/tweet?url=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2"><span className="w-11 h-11 rounded-full bg-navy-50 grid place-items-center text-navy"><X className="w-5 h-5" /></span>X</a>
            </div>
            {shareMessage && <p role="status" className="text-xs text-blue-700 mt-3">{shareMessage}</p>}
          </section>
          {!isOwner && <section className="card p-4"><ReportButton targetType="job" targetId={job.id} targetUserId={job.ownerUserId || undefined} className="[&>button]:border-0" /></section>}
        </aside>
      </div>
      {!isMobileViewport && !isOwner && !isInPageApplyVisible && <MobileActionBar><div className="shrink-0"><div className="text-[11px] text-mut leading-none">Salary</div><div className="text-base font-extrabold text-navy leading-tight">{job.salary || "Negotiable"}</div></div><div className="flex-1"><ApplyBox /></div></MobileActionBar>}
    </div>}
    {showApply && <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setShowApply(false)}>
        <div className="card p-5 max-w-md w-full" onClick={(event) => event.stopPropagation()}>
          <div className="flex items-center justify-between mb-3"><h2 className="font-bold text-navy">Apply — {job.title}</h2><button type="button" onClick={() => setShowApply(false)} className="btn-ghost h-8 w-8 p-0" aria-label="Close"><X className="w-4 h-4" /></button></div>
          <p className="text-sm text-mut mb-3">{t("activity.resumeHelp")}</p>
          <button type="button" onClick={() => setShowResumes(true)} disabled={busy} className="btn-outline w-full mb-3 disabled:opacity-60">{t("activity.manageResume")}</button>
          <label className="label" htmlFor="job-cover-note">Cover note (optional)</label>
          <textarea id="job-cover-note" className="field min-h-[120px]" value={coverNote} onChange={(event) => setCoverNote(event.target.value)} placeholder="Tell the employer why you're a great fit…" />
          {err && <p className="text-sm text-brand-red mt-2">{err}</p>}
          <button type="button" onClick={apply} disabled={busy} className="btn-red w-full mt-3 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Submit application</button>
        </div>
    </div>}
    <ResumeModal open={showResumes} onClose={() => setShowResumes(false)} onChanged={() => setErr(null)} />
    </>
  );
}
